import React from "react";
import { motion } from "framer-motion";
import SafeIcon from "../common/SafeIcon";
import * as FiIcons from "react-icons/fi";

const { FiWind, FiZap, FiThermometer, FiSettings, FiDroplet, FiCpu } = FiIcons;

/**
 * HowGreenGasTurbinesWork – Technical explainer with educational depth
 * Provides detailed, illustrated explanation of how green gas turbines operate.
 * Targets informational search intent and adds unique content for AdSense.
 */
export default function HowGreenGasTurbinesWork() {
  const steps = [
    {
      icon: FiWind,
      step: "1",
      title: "Air Compression",
      description:
        "Ambient air enters the compressor section, where a series of rotating and stationary blade rows progressively increase its pressure by 15:1 to 35:1. Modern axial compressors achieve isentropic efficiencies above 90%, minimising the parasitic power consumed by compression.",
    },
    {
      icon: FiDroplet,
      step: "2",
      title: "Fuel Injection & Mixing",
      description:
        "Low-carbon fuel—hydrogen, RNG, or a blend—is injected into the combustion chamber through precision nozzles. In dry-low-NOx (DLN) systems, fuel and air are premixed before ignition to create a lean, uniform flame that keeps peak temperatures below the thermal-NOx formation threshold of ~1,800 K.",
    },
    {
      icon: FiThermometer,
      step: "3",
      title: "Combustion",
      description:
        "The fuel-air mixture ignites at temperatures between 1,200 °C and 1,600 °C, producing high-energy exhaust gases. Hydrogen burns 6–8× faster than methane, so green gas turbines use advanced flame-holding geometries and active fuel staging to prevent flashback while maintaining stable combustion across the full 0–100% H₂ range.",
    },
    {
      icon: FiZap,
      step: "4",
      title: "Turbine Expansion & Power Generation",
      description:
        "Hot gases expand through the turbine section, spinning the rotor at 3,000–3,600 RPM. This mechanical energy drives both the compressor (via a shared shaft) and an electrical generator. Single-shaft industrial turbines convert 38–44% of fuel energy into electricity in simple-cycle mode.",
    },
    {
      icon: FiSettings,
      step: "5",
      title: "Heat Recovery (CHP / Combined Cycle)",
      description:
        "Exhaust gases exit the turbine at 450–600 °C—still hot enough to raise steam in a heat-recovery steam generator (HRSG). This steam can drive a secondary steam turbine (combined cycle, boosting electrical efficiency to 60–64%) or supply industrial process heat, district heating, or absorption chillers (CHP mode, total efficiency >90%).",
    },
    {
      icon: FiCpu,
      step: "6",
      title: "Digital Controls & Emissions Monitoring",
      description:
        "Edge-based control systems continuously monitor flame temperature, pressure pulsations, NOx/CO concentrations, and vibration. Machine-learning algorithms adjust fuel splits and inlet guide vanes in real time, optimising efficiency and emissions across varying loads, ambient conditions, and hydrogen blend ratios.",
    },
  ];

  return (
    <section id="how-it-works" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-4xl mx-auto text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-secondary-900 mb-6">
            How Green Gas Turbines Work: A Technical Overview
          </h2>
          <p className="text-lg md:text-xl text-secondary-600 leading-relaxed">
            Understanding the thermodynamic cycle behind hydrogen-ready gas turbines helps
            engineers and decision-makers evaluate efficiency, emissions, and integration
            options. Below is a step-by-step breakdown of the Brayton cycle as implemented
            in modern green gas turbines.
          </p>
        </motion.div>

        {/* Step-by-step process */}
        <div className="relative">
          {/* Vertical connector line (desktop) */}
          <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-primary-200 -translate-x-1/2" />

          <div className="space-y-12 lg:space-y-16">
            {steps.map((item, idx) => {
              const isEven = idx % 2 === 0;
              return (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, x: isEven ? -40 : 40 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: idx * 0.08 }}
                  className={`relative flex flex-col lg:flex-row items-center gap-6 lg:gap-12 ${
                    isEven ? "lg:flex-row" : "lg:flex-row-reverse"
                  }`}
                >
                  {/* Content card */}
                  <div className="flex-1 bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="flex items-center justify-center w-12 h-12 bg-primary-100 rounded-xl">
                        <SafeIcon icon={item.icon} className="h-6 w-6 text-primary-600" />
                      </div>
                      <h3 className="text-xl font-bold text-secondary-900">{item.title}</h3>
                    </div>
                    <p className="text-secondary-600 leading-relaxed">{item.description}</p>
                  </div>

                  {/* Step number (centre on desktop) */}
                  <div className="order-first lg:order-none flex-shrink-0 w-14 h-14 rounded-full bg-primary-600 text-white flex items-center justify-center text-xl font-bold shadow-lg z-10">
                    {item.step}
                  </div>

                  {/* Spacer for alternating layout */}
                  <div className="hidden lg:block flex-1" />
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Supplementary prose for content depth */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-20 max-w-4xl mx-auto"
        >
          <div className="prose prose-lg prose-secondary max-w-none text-secondary-700 leading-relaxed space-y-6">
            <h3 className="text-2xl font-bold text-secondary-900">
              Why Hydrogen Changes the Combustion Equation
            </h3>
            <p>
              Hydrogen's lower heating value (LHV) is <strong>120 MJ/kg</strong>—roughly three times
              that of natural gas on a mass basis—but its volumetric energy density is only about
              one-third of methane. This means fuel systems must handle roughly three times the
              volumetric flow rate when switching from natural gas to 100% hydrogen. Fuel nozzles,
              control valves, and piping must be resized, and fuel compressors may need upgrading.
            </p>
            <p>
              The higher flame speed of hydrogen (approximately <strong>3.5 m/s</strong> laminar
              burning velocity versus 0.4 m/s for methane) creates a risk of <em>flashback</em>—where
              the flame propagates upstream into the premixing zone. Modern DLN burners counter this
              with high-velocity air jets, swirl-stabilised flame anchoring, and real-time fuel
              staging that adjusts the hydrogen-to-air ratio hundreds of times per second.
            </p>
            <p>
              Despite these engineering challenges, the payoff is substantial. A 50 MW green gas
              turbine running on 100% green hydrogen eliminates approximately{" "}
              <strong>120,000 tonnes of CO₂ per year</strong> compared to the same unit burning fossil
              natural gas—equivalent to taking 26,000 cars off the road. Even a modest 30% hydrogen
              blend reduces annual CO₂ by roughly 36,000 tonnes while requiring only minor hardware
              modifications.
            </p>

            <h3 className="text-2xl font-bold text-secondary-900 mt-10">
              Efficiency Considerations: Simple Cycle vs. Combined Cycle vs. CHP
            </h3>
            <p>
              The choice of cycle configuration depends on the application. <strong>Simple-cycle</strong>{" "}
              plants (38–44% efficiency) are ideal for peaking duty where fast start-up matters more
              than fuel economy. <strong>Combined-cycle</strong> plants add a steam bottoming cycle to
              reach 60–64% efficiency, making them the most fuel-efficient fossil-thermal technology
              available. <strong>CHP installations</strong> capture exhaust heat for industrial
              processes or district heating, achieving total fuel utilisation rates above 90%—the
              highest of any thermal generation technology.
            </p>
            <p>
              Green gas turbines maintain these efficiency levels across hydrogen blend ratios. In
              fact, hydrogen's higher flame temperature can slightly increase turbine inlet
              temperature (TIT), marginally boosting electrical output. However, operators must
              balance this against increased thermal stress on hot-section components, which may
              shorten maintenance intervals if not managed with advanced coatings and cooling schemes.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
